import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';
import { useBrand } from '../store/BrandContext';

export function PwaInstallPrompt() {
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(true);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const { brandConfig } = useBrand();

  useEffect(() => {
    // Check if device is iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const ios = /iphone|ipad|ipod/.test(userAgent);
    setIsIOS(ios);

    // Check if app is already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone === true;
    setIsStandalone(standalone);

    if (standalone) return;

    // Android/Chrome: capture the install event as soon as it fires — the
    // "Instalar" button is dead without a stored deferredPrompt. This must
    // run in non-standalone mode (previously the listener was registered
    // only when already installed, so the button never worked).
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!localStorage.getItem('pwa_prompt_dismissed')) {
        setShowPrompt(true);
      }
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Once installed via the prompt, never show again (this tab included).
    const handleAppInstalled = () => {
      localStorage.setItem('pwa_installed', 'true');
      setShowPrompt(false);
      setDeferredPrompt(null);
    };
    window.addEventListener('appinstalled', handleAppInstalled);

    // Fallback for browsers without beforeinstallprompt (iOS Safari):
    // still surface the banner so iOS users get manual instructions.
    // On Chromium/Firefox the event only fires when the app is installable —
    // i.e. NOT already installed — so no event after 3s ⇒ installed (or not
    // installable) ⇒ don't pester. iOS can't detect an installed PWA from a
    // tab, so it keeps the manual-instructions banner.
    const timer = setTimeout(() => {
      if (!ios) return;
      const hasDismissed = localStorage.getItem('pwa_prompt_dismissed');
      const installed = localStorage.getItem('pwa_installed');
      if (!hasDismissed && !installed) {
        setShowPrompt(true);
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(timer);
    };
  }, []);

  if (isStandalone || !showPrompt) return null;

  const handleInstall = async () => {
    if (deferredPrompt) {
      // Android/Chrome install
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else if (isIOS) {
      // Just show instructions for iOS
      alert("Para instalar: toque em 'Partilhar' no fundo do ecrã e depois em 'Adicionar ao Ecrã Principal'.");
    } else {
      // Browser doesn't expose a programmatic install prompt (or the event
      // hasn't fired yet) — guide the user to the browser's own install UI.
      alert("Para instalar: usa o menu do navegador (⋮ ou ⋯) e escolhe 'Instalar aplicação' ou 'Adicionar ao ecrã principal'.");
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa_prompt_dismissed', 'true');
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 p-4 z-[9999] animate-in slide-in-from-bottom-full duration-500">
      <div className="max-w-md mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-4 flex items-center gap-4">
        {brandConfig?.logo_url ? (
          <img src={brandConfig.logo_url} alt="Logo" className="w-12 h-12 rounded-xl object-cover bg-slate-100" />
        ) : (
          <div className="w-12 h-12 bg-brand/10 text-brand rounded-xl flex items-center justify-center shrink-0">
            <Download className="w-6 h-6" />
          </div>
        )}
        
        <div className="flex-1">
          <h3 className="font-bold text-sm text-slate-900 dark:text-white">Instale a app</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Acesso rápido e offline</p>
        </div>

        <button 
          onClick={handleInstall}
          className="bg-brand text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg shadow-brand/20 hover:scale-105 transition-transform"
        >
          Instalar
        </button>

        <button onClick={handleDismiss} className="p-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200">
          <X className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}
